import { useCallback, useState } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

export interface WizardStep<TForm extends FieldValues> {
  key: string;
  label: string;
  fields: ReadonlyArray<Path<TForm>>;
  /** Optional step — Next button is joined by a Skip button when every field is truly optional. */
  optional?: boolean;
}

export interface UseWizardStateOptions<TForm extends FieldValues> {
  steps: ReadonlyArray<WizardStep<TForm>>;
  form: UseFormReturn<TForm>;
  /** Client-side mirror of the server's canActivate(). Returning ok=false
   *  disables the Activate button and surfaces missing fields. */
  canActivate: (values: TForm) => { ok: boolean; missing: ReadonlyArray<Path<TForm>> };
}

export interface UseWizardStateResult<TForm extends FieldValues> {
  currentStep: number;
  step: WizardStep<TForm>;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Request a step change. For forward jumps, runs `trigger(stepFields)` on all
   *  intervening steps and blocks at the first invalid one. Backward jumps are
   *  unconditional. */
  goToStep: (index: number) => Promise<boolean>;
  next: () => Promise<boolean>;
  previous: () => void;
  canActivate: ReturnType<UseWizardStateOptions<TForm>['canActivate']>;
  /** Revalidate the current step's fields. Useful on step ENTRY so users don't
   *  walk into a silently-invalid step (spec §3.3). */
  revalidateCurrentStep: () => Promise<boolean>;
}

/**
 * Step progression + per-step validation via RHF's `trigger(fieldNames)`.
 * Spec §3.3 — one Zod schema, field lists per step, no sub-schema duplication.
 */
export function useWizardState<TForm extends FieldValues>({
  steps,
  form,
  canActivate,
}: UseWizardStateOptions<TForm>): UseWizardStateResult<TForm> {
  const [currentStep, setCurrentStep] = useState(0);

  const goToStep = useCallback(
    async (index: number): Promise<boolean> => {
      if (index < 0 || index >= steps.length) return false;
      // Backward: free
      if (index <= currentStep) {
        setCurrentStep(index);
        // Revalidate on entry so stale errors surface.
        await form.trigger(steps[index]!.fields as Path<TForm>[]);
        return true;
      }
      // Forward: validate every step between current and target, inclusive of
      // current but exclusive of target (target's own fields are user's next job).
      for (let i = currentStep; i < index; i++) {
        const ok = await form.trigger(steps[i]!.fields as Path<TForm>[]);
        if (!ok) {
          setCurrentStep(i);
          return false;
        }
      }
      setCurrentStep(index);
      await form.trigger(steps[index]!.fields as Path<TForm>[]);
      return true;
    },
    [currentStep, form, steps],
  );

  const next = useCallback(() => goToStep(currentStep + 1), [goToStep, currentStep]);
  const previous = useCallback(() => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const revalidateCurrentStep = useCallback(
    () => form.trigger(steps[currentStep]!.fields as Path<TForm>[]),
    [form, steps, currentStep],
  );

  return {
    currentStep,
    step: steps[currentStep]!,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === steps.length - 1,
    goToStep,
    next,
    previous,
    canActivate: canActivate(form.getValues()),
    revalidateCurrentStep,
  };
}
